import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import type { SupportingPagesByTopicSnapshotData } from '../services/dataforseo';
import type { DomainTopicsService } from '../services/domain-topics';
import {
  extractTopicTaggedPagesFromDiagnostic,
  filterRowsByFocalEntity,
  filterSupportingPagesByRelevance,
  getFocalAnchorTopicSlugs,
  isComparisonLikeUrlForTopicAnchors,
  mergeTopicTaggedPagesByUrl,
  normalizeTopicSlug,
  resolveParentTopicSlug,
  compareSupportingPagesByRelevance,
  type TopicParentLookup,
  type TopicSlugLabel,
  FOCAL_ANCHOR_COMPARISON_MIN_RELATIVE_WEIGHT
} from './topic-supporting-pages';

export interface SupportingPagesPresenterPage {
  url: string;
  seoIntentScore: number;
  estimatedOrganicTraffic?: number;
}

export interface SupportingPagesPresenterTopic {
  topicSlug: string;
  topicLabel: string;
  yourPages: SupportingPagesPresenterPage[];
  theirPages: SupportingPagesPresenterPage[];
}

export interface SupportingPagesPresenterModel {
  topics: SupportingPagesPresenterTopic[];
  anchorTopicLabels: string[];
  source: 'snapshot' | 'live' | 'empty';
}

/**
 * Live-path anchors (used when no snapshot exists). Site-wide anchor prevalence
 * is a whole-domain measurement the client does not hold, so ambient head-term
 * demotion (findings §15) only happens in the snapshot build; the slug-specific
 * co-anchor rescue (findings §24) works from the focal URL alone and applies here.
 */
export function resolveFocalAnchorTopics(
  analysis: { topic?: { topicSignals?: unknown } } | null | undefined,
  focalUrl?: string
): { slugs: Set<string>; labels: string[]; topics: TopicSlugLabel[] } {
  const isComparisonLike = isComparisonLikeUrlForTopicAnchors(focalUrl || '');
  return getFocalAnchorTopicSlugs(analysis, {
    ...(isComparisonLike ? { minRelativeWeight: FOCAL_ANCHOR_COMPARISON_MIN_RELATIVE_WEIGHT } : {}),
    ...(focalUrl ? { focalUrl } : {})
  });
}

function normalizeDomainKey(domain: string): string {
  return domain.toLowerCase().trim().replace(/^www\./, '');
}

function pagesFromSnapshotRows(
  rows: SupportingPagesByTopicSnapshotData['domains'][number]['rows'] | undefined,
  topicSlug: string
): SupportingPagesPresenterPage[] {
  const slugKey = normalizeTopicSlug(topicSlug);
  if (!rows?.length || !slugKey) return [];
  return rows
    .filter((row) => row.topics.some((chip) => normalizeTopicSlug(chip.topicSlug) === slugKey))
    .sort(compareSupportingPagesByRelevance)
    .slice(0, 5)
    .map((row) => ({
      url: row.url,
      seoIntentScore: row.seoIntentScore,
      estimatedOrganicTraffic: row.estimatedOrganicTraffic
    }));
}

export function buildSupportingPagesFromSnapshot(input: {
  snapshot: SupportingPagesByTopicSnapshotData;
  theirDomain: string;
}): SupportingPagesPresenterModel {
  const theirKey = normalizeDomainKey(input.theirDomain);
  const yourBlock = input.snapshot.domains.find((block) => block.isYourSite);
  const theirBlock = input.snapshot.domains.find(
    (block) => !block.isYourSite && normalizeDomainKey(block.domain) === theirKey
  );
  const slugs = input.snapshot.focalAnchorTopicSlugs || [];
  const labels = input.snapshot.focalAnchorTopicLabels || [];

  const topics: SupportingPagesPresenterTopic[] = slugs.map((slug, index) => ({
    topicSlug: slug,
    topicLabel: labels[index] || slug,
    yourPages: pagesFromSnapshotRows(yourBlock?.rows, slug),
    theirPages: pagesFromSnapshotRows(theirBlock?.rows, slug)
  }));

  return {
    topics: topics.filter((topic) => topic.yourPages.length > 0 || topic.theirPages.length > 0),
    anchorTopicLabels: labels,
    source: 'snapshot'
  };
}

function extractPagesFromDiagnostic(res: unknown): SupportingPagesPresenterPage[] {
  const payload = res as {
    data?: { coverage?: { directlyCoveredTopics?: unknown[] } };
  } | null;
  if (!payload?.data?.coverage?.directlyCoveredTopics) return [];
  const topics: any[] = payload.data.coverage.directlyCoveredTopics;
  const allUrls: SupportingPagesPresenterPage[] = [];
  for (const topic of topics) {
    const pages = [
      ...(Array.isArray(topic.corePages) ? topic.corePages : []),
      ...(Array.isArray(topic.mentionPages) ? topic.mentionPages : [])
    ];
    for (const page of pages) {
      if (!page?.url) continue;
      allUrls.push({
        url: page.url,
        seoIntentScore: Number.isFinite(page.seoIntentScore) ? page.seoIntentScore : 0,
        estimatedOrganicTraffic:
          typeof page.estimatedOrganicTraffic === 'number' ? page.estimatedOrganicTraffic : undefined
      });
    }
  }
  return allUrls.sort(compareSupportingPagesByRelevance).slice(0, 5);
}

function buildLiveTopics(input: {
  focalUrl: string;
  anchorTopics: TopicSlugLabel[];
  parentTopicsByDomain: { your: TopicParentLookup[]; their: TopicParentLookup[] };
  domainTopicsService: DomainTopicsService;
  yourDomain: string;
  theirDomain: string;
}): Observable<SupportingPagesPresenterModel> {
  if (!input.anchorTopics.length) {
    return of({ topics: [], anchorTopicLabels: [], source: 'empty' });
  }

  const targetTopicSlugs = input.anchorTopics.map((topic) => normalizeTopicSlug(topic.slug));
  const topicLabelsBySlug = new Map(
    input.anchorTopics.map((topic) => [normalizeTopicSlug(topic.slug), topic.label])
  );

  const topicCalls = input.anchorTopics.map((topic) => {
    const parentYour = resolveParentTopicSlug(topic.slug, input.parentTopicsByDomain.your);
    const parentTheir = resolveParentTopicSlug(topic.slug, input.parentTopicsByDomain.their);
    return forkJoin({
      your: input.domainTopicsService
        .getTopicDiagnostic(input.yourDomain, parentYour)
        .pipe(catchError(() => of(null))),
      their: input.domainTopicsService
        .getTopicDiagnostic(input.theirDomain, parentTheir)
        .pipe(catchError(() => of(null))),
      meta: of(topic)
    });
  });

  return forkJoin(topicCalls).pipe(
    map((results) => {
      const topics: SupportingPagesPresenterTopic[] = results.map((result) => {
        const yourTagged = extractTopicTaggedPagesFromDiagnostic(result.your, {
          targetTopicSlugs,
          topicLabelsBySlug
        });
        const theirTagged = extractTopicTaggedPagesFromDiagnostic(result.their, {
          targetTopicSlugs,
          topicLabelsBySlug
        });
        const yourPages = filterSupportingPagesByRelevance(mergeTopicTaggedPagesByUrl(yourTagged))
          .sort(compareSupportingPagesByRelevance)
          .slice(0, 5)
          .map((row) => ({
            url: row.url,
            seoIntentScore: row.seoIntentScore,
            estimatedOrganicTraffic: row.estimatedOrganicTraffic
          }));
        // FW-ENTITY-01: a rival page about a different product is not a peer.
        const theirPages = filterRowsByFocalEntity(
          filterSupportingPagesByRelevance(mergeTopicTaggedPagesByUrl(theirTagged)),
          input.focalUrl
        )
          .sort(compareSupportingPagesByRelevance)
          .slice(0, 5)
          .map((row) => ({
            url: row.url,
            seoIntentScore: row.seoIntentScore,
            estimatedOrganicTraffic: row.estimatedOrganicTraffic
          }));
        return {
          topicSlug: result.meta.slug,
          topicLabel: result.meta.label,
          yourPages,
          theirPages
        };
      });

      return {
        topics: topics.filter((topic) => topic.yourPages.length > 0 || topic.theirPages.length > 0),
        anchorTopicLabels: input.anchorTopics.map((topic) => topic.label),
        source: 'live' as const
      };
    }),
    catchError(() => of({ topics: [], anchorTopicLabels: [], source: 'empty' as const }))
  );
}

export function loadSupportingPagesContext(input: {
  mode: 'snapshot' | 'live';
  focalUrl: string;
  focalAnalysis: { topic?: { topicSignals?: unknown } } | null | undefined;
  yourDomain: string;
  theirDomain: string;
  snapshot?: SupportingPagesByTopicSnapshotData | null;
  domainTopicsService?: DomainTopicsService;
  parentTopicsByDomain?: { your: TopicParentLookup[]; their: TopicParentLookup[] };
}): Observable<SupportingPagesPresenterModel> {
  const anchor = resolveFocalAnchorTopics(input.focalAnalysis, input.focalUrl);
  const anchorTopics = anchor.topics;

  if (input.mode === 'snapshot' && input.snapshot?.focalAnchorTopicSlugs?.length) {
    return of(buildSupportingPagesFromSnapshot({ snapshot: input.snapshot, theirDomain: input.theirDomain }));
  }

  if (!input.domainTopicsService || !input.parentTopicsByDomain) {
    return of({
      topics: [],
      anchorTopicLabels: anchor.labels,
      source: 'empty'
    });
  }

  return buildLiveTopics({
    focalUrl: input.focalUrl,
    anchorTopics,
    parentTopicsByDomain: input.parentTopicsByDomain,
    domainTopicsService: input.domainTopicsService,
    yourDomain: input.yourDomain,
    theirDomain: input.theirDomain
  });
}
